import { useState } from "react";
import { motion } from "framer-motion";
import { personalInfo } from "../data/personalInfo";
import { socialLinks } from "../data/socialLinks";
import Button from "./Button";
import SectionTitle from "./SectionTitle";
import { Mail, Phone, MapPin, Send, CheckCircle2, AlertCircle, Copy, Check } from "lucide-react";

const Contact = () => {
  const [formState, setFormState] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  
  const [isSending, setIsSending] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [copied, setCopied] = useState(false);

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(personalInfo.email);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSending(true);
    setSubmitStatus(null);

    const formData = new FormData();
    formData.append("name", formState.name);
    formData.append("email", formState.email);
    formData.append("subject", formState.subject);
    formData.append("message", formState.message);
    formData.append("access_key", "6ef8b728-b7c0-4136-a578-28482d76312f");

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        setSubmitStatus("success");
        setFormState({ name: "", email: "", subject: "", message: "" });
      } else {
        setSubmitStatus("error");
      }
    } catch (error) {
      console.error("Submission error:", error);
      setSubmitStatus("error");
    } finally {
      setIsSending(false);
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gray-50/30 dark:bg-gray-950/20 relative">
      <div className="container mx-auto px-4 sm:px-6">
        <SectionTitle
          title="Get In Touch"
          subtitle="Have a project in mind, contract inquiry, or full-time opportunity?"
        />
        
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-5xl mx-auto mt-12 bg-white dark:bg-gray-900 rounded-3xl shadow-2xl overflow-hidden border border-gray-200/80 dark:border-gray-800"
        >
          <div className="flex flex-col md:flex-row items-stretch">
            {/* Contact Info Sidebar */}
            <div className="w-full md:w-5/12 bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-800 p-8 sm:p-10 text-white flex flex-col justify-between relative overflow-hidden">
              <div className="absolute -bottom-10 -right-10 w-48 h-48 bg-white/10 rounded-full blur-2xl pointer-events-none"></div>

              <div>
                <h3 className="text-2xl font-bold font-heading mb-3">Let's Connect</h3>
                <p className="text-indigo-100/90 mb-8 leading-relaxed text-sm">
                  Send a message directly via the form or copy my email address for your preferred mail app.
                </p>
                
                <div className="space-y-6">
                  {/* Email with copy */}
                  <div className="flex items-start space-x-3.5">
                    <div className="rounded-xl bg-white/10 p-2.5 mt-0.5 border border-white/10 shrink-0">
                      <Mail className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-bold text-[10px] text-indigo-200 uppercase tracking-widest">Email</p>
                        <button
                          onClick={handleCopyEmail}
                          className="text-xs text-indigo-200 hover:text-white flex items-center gap-1 transition-colors cursor-pointer"
                        >
                          {copied ? (
                            <span className="text-emerald-300 font-semibold flex items-center gap-1">
                              <Check className="w-3.5 h-3.5" /> Copied!
                            </span>
                          ) : (
                            <span className="flex items-center gap-1">
                              <Copy className="w-3.5 h-3.5" /> Copy
                            </span>
                          )}
                        </button>
                      </div>
                      <a href={`mailto:${personalInfo.email}`} className="text-white font-semibold hover:underline text-sm break-all">
                        {personalInfo.email}
                      </a>
                    </div>
                  </div>
                  
                  {personalInfo.phone && (
                    <div className="flex items-start space-x-3.5">
                      <div className="rounded-xl bg-white/10 p-2.5 mt-0.5 border border-white/10 shrink-0">
                        <Phone className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="font-bold text-[10px] text-indigo-200 uppercase tracking-widest">Phone</p>
                        <a href={`tel:${personalInfo.phone}`} className="text-white font-semibold hover:underline text-sm">
                          {personalInfo.phone}
                        </a>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-start space-x-3.5">
                    <div className="rounded-xl bg-white/10 p-2.5 mt-0.5 border border-white/10 shrink-0">
                      <MapPin className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-bold text-[10px] text-indigo-200 uppercase tracking-widest">Location</p>
                      <p className="text-white font-semibold text-sm">
                        {personalInfo.location}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Sidebar Social Links */}
              <div className="mt-10 pt-6 border-t border-white/10">
                <p className="font-bold text-[10px] text-indigo-200 uppercase tracking-widest mb-3.5">Connect Socially</p>
                <div className="flex space-x-3">
                  {socialLinks.map((link, index) => (
                    <a 
                      key={index}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-white/10 hover:bg-white/20 p-3 rounded-xl border border-white/10 transition-all hover:scale-105 text-white flex items-center justify-center"
                      aria-label={link.name}
                    >
                      <span className="text-lg">{link.icon}</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Contact Form */}
            <div className="w-full md:w-7/12 p-8 sm:p-10 flex flex-col justify-center">
              {submitStatus === "success" && (
                <div className="mb-6 p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-900/50 flex items-center space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                  <span className="text-sm font-semibold">Your message was sent successfully! Matin will reply soon.</span>
                </div>
              )}

              {submitStatus === "error" && (
                <div className="mb-6 p-4 rounded-2xl bg-rose-50 dark:bg-rose-950/40 text-rose-800 dark:text-rose-300 border border-rose-200 dark:border-rose-900/50 flex items-center space-x-3">
                  <AlertCircle className="h-5 w-5 text-rose-600 dark:text-rose-400 shrink-0" />
                  <span className="text-sm font-semibold">Failed to deliver message. Please email directly.</span>
                </div>
              )}

              <form className="space-y-5" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5"
                    >
                      Your Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formState.name}
                      onChange={handleChange}
                      placeholder="e.g. Alex Smith"
                      className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 outline-none transition-colors text-sm dark:text-white"
                      required
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5"
                    >
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formState.email}
                      onChange={handleChange}
                      placeholder="alex@company.com"
                      className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 outline-none transition-colors text-sm dark:text-white"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label
                    htmlFor="subject"
                    className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5"
                  >
                    Subject / Project Type
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formState.subject}
                    onChange={handleChange}
                    placeholder="e.g. React Native Mobile App Project"
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 outline-none transition-colors text-sm dark:text-white"
                    required
                  />
                </div>
                
                <div>
                  <label
                    htmlFor="message"
                    className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5"
                  >
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formState.message}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Tell me a bit about your project goals or team inquiry..."
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 outline-none transition-colors resize-none text-sm dark:text-white"
                    required
                  ></textarea>
                </div>
                
                <div className="pt-2">
                  <Button
                    type="submit"
                    variant="primary"
                    className="w-full py-3.5 rounded-xl text-white font-semibold hover:shadow-lg shadow-md transition-all duration-300 flex justify-center items-center text-sm"
                    disabled={isSending}
                  >
                    {isSending ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2.5 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending message...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;